import { createContext, useContext, useEffect, useState, useRef, ReactNode, useMemo } from "react";
import { User, Session } from "@supabase/supabase-js";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { captureAuthError } from "@/lib/sentryAuth";
import { isPasswordAuthAllowed, isPasswordAuthAllowedSync } from "@/lib/authMode";
import { isReadOnlyDefectRole } from "@/lib/defectRoleAccess";
import {
  isExternalReadOnly,
  isExternalLimitedWrite,
  isExternalDefectUser,
} from "@/lib/externalRoleAccess";
import i18n from "@/i18n";
import { clearImageCache } from "@/components/ui/AuthenticatedImage";
import { showLogoutTransition } from "@/lib/logoutTransition";
import { provisionExternalIdentity } from "@/lib/externalProvisioning";
import {
  useAuthProfile,
  useAuthRoles,
  useAuthTenantCount,
  useAuthTenantMembership,
  authKeys,
  type AppRole,
  type Profile,
} from "./useAuthQueries";

// Session storage key to prevent duplicate login events on page refresh
const LOGIN_LOGGED_KEY = "login_event_logged";

function shouldCheckLocalMfa(provider: string) {
  return provider === "email" && isPasswordAuthAllowedSync();
}

// SSO/Entra providers that can carry external Entra B2B identity claims. Local password
// logins ("email") never go through JIT external provisioning.
function isSsoProvider(provider: string): boolean {
  return provider !== "email";
}

// External-identity JIT provisioning (TES-598) lives in src/lib/externalProvisioning.ts so its
// branching logic is unit-testable in isolation (it is invoked from the SSO auth-state handler below).

async function clearSessionQueryCache(queryClient: QueryClient) {
  await queryClient.cancelQueries();
  queryClient.clear();
}

/**
 * Log a login event to the audit_log table.
 * Uses session storage to prevent duplicate entries on page refresh.
 *
 * IMPORTANT: This function is completely fire-and-forget and will never
 * throw or block the auth flow, even if RLS denies the insert (403).
 */
function logLoginEvent(userId: string, provider: string, email: string, tenantId: string | null) {
  // Prevent duplicate logging on page refresh
  try {
    const loggedSessionId = sessionStorage.getItem(LOGIN_LOGGED_KEY);
    if (loggedSessionId === userId) {
      return; // Already logged this session
    }
  } catch {
    // sessionStorage might be blocked in some contexts (iframe, privacy mode)
    // Continue anyway - the worst case is a duplicate log entry
  }

  // Structured success log — IDs/provider only, never the e-mail or any token (PII-safe).
  logger.info("Auth login event", {
    user_id: userId,
    tenant_id: tenantId,
    provider,
  });

  // Fire-and-forget: wrap in Promise.resolve to get proper Promise methods
  Promise.resolve(
    supabase.from("audit_log").insert({
      entity_type: "user_session",
      entity_id: userId,
      action: "login",
      new_values: {
        provider,
        email,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
      },
      user_id: userId,
      tenant_id: tenantId,
    }),
  )
    .then(() => {
      // Mark as logged for this session
      try {
        sessionStorage.setItem(LOGIN_LOGGED_KEY, userId);
      } catch {
        // Ignore sessionStorage errors
      }
    })
    .catch((error) => {
      // Silently fail - login should NEVER be blocked by audit logging failures
      logger.warn("Failed to log login event (non-blocking):", error);
    });
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  reloadUserData: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  isAdmin: boolean;
  isQualitaetsmanager: boolean;
  isMangelbearbeiter: boolean;
  isTester: boolean;
  /**
   * Whether the user holds the role_id-only QM-Admin role (roles.key='qm_admin', mig 320/321 —
   * TES-1833 / GOAL A1). QM-Admin owns the org hull (groups/owners/standorte) and is NOT operative.
   * Resolved via fetchRoles' roles(key) embed; mirrors the is_qm_admin() RLS helper (mig 328).
   */
  isQmAdmin: boolean;
  /** Check if user is a super admin (platform-wide access) */
  isSuperAdmin: boolean;
  /** Check if user is a tenant admin for the active tenant */
  isTenantAdmin: boolean;
  /** Check if user can manage inspections, checklists etc. (admin or QM) */
  canManage: boolean;
  /**
   * Whether the user is restricted to read-only defect access. True for the
   * internal customer_approver / Kundenabnahme role without any defect-editing role
   * (TES-565) AND for fully read-only external identities (partner_supplier /
   * external_auditor — TES-626). Such users see no status-change controls.
   */
  isReadOnlyDefectUser: boolean;
  /**
   * Whether the user is a FULLY read-only external identity (external_user with
   * external_category partner_supplier / external_auditor — TES-626). No write
   * controls at all (incl. comments / evidence).
   */
  isExternalReadOnlyUser: boolean;
  /**
   * Whether the user is an external customer_acceptance identity (TES-626) — may
   * comment + append evidence on anchored defects, but operative fields stay locked.
   */
  isExternalLimitedWriteUser: boolean;
  /**
   * Whether the user is ANY external identity at the defect boundary (read-only OR
   * limited-write). Drives the "read-only external view" indication (TES-626).
   */
  isExternalDefectUser: boolean;
  /** Number of tenants the user belongs to */
  tenantCount: number;
  /** Whether the user needs to complete MFA verification */
  mfaRequired: boolean;
  /** Whether the user needs to enroll MFA before accessing the app */
  mfaEnrollmentRequired: boolean;
  /** Whether the user's registration is pending approval by a Super-Admin */
  registrationPending: boolean;
  /** Call after successful MFA verification or enrollment to unlock the app */
  completeMfa: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // Core auth state - only user/session are managed manually
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [mfaChecking, setMfaChecking] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);

  // Track if we've logged the current session's login event
  const loginLoggedRef = useRef<boolean>(false);

  // TanStack Query hooks for all auth data
  // These automatically handle:
  // - Request deduplication
  // - Abort on key change (prevents race conditions)
  // - Caching with staleTime
  // - Automatic retries
  const userId = user?.id ?? null;

  const {
    data: profile,
    isLoading: profileLoading,
    refetch: refetchProfile,
  } = useAuthProfile(userId);

  const activeTenantId = profile?.active_tenant_id ?? null;

  const { data: roles = [], isLoading: rolesLoading } = useAuthRoles(userId, activeTenantId);

  const { data: tenantCount = 0, isLoading: tenantCountLoading } = useAuthTenantCount(userId);

  const { data: tenantMembership, isSuccess: tenantMembershipLoaded } = useAuthTenantMembership(
    userId,
    activeTenantId,
  );

  useEffect(() => {
    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Set Sentry user context for Crash Free Users metrics
        Sentry.setUser({
          id: session.user.id,
          email: session.user.email || undefined,
        });

        // Invalidate all auth queries to trigger fresh fetch with new user
        // TanStack Query handles deduplication automatically
        queryClient.invalidateQueries({ queryKey: authKeys.all });

        // Log login event on SIGNED_IN (not on TOKEN_REFRESHED or INITIAL_SESSION)
        // IMPORTANT: This must be non-blocking (no await) to prevent auth flow from hanging
        if (event === "SIGNED_IN" && !loginLoggedRef.current) {
          loginLoggedRef.current = true;
          const provider = session.user.app_metadata?.provider || "email";
          // Fire-and-forget: Don't await, don't block auth flow
          // tenant_id will be null initially - that's acceptable for login logging
          logLoginEvent(
            session.user.id,
            provider,
            session.user.email || "",
            null, // Don't fetch profile here - avoid blocking the auth flow
          );

          // JIT-provision external Entra B2B users right after SSO login (TES-598).
          // Fire-and-forget for internal users (200 no-op); the helper signs out on a
          // 401 deny. Never awaited — must not block or break a normal login.
          if (isSsoProvider(provider) && session.access_token) {
            void provisionExternalIdentity(session.access_token);
          }

          // Check MFA only for local password users. Production Entra/SSO
          // sessions are handled by the IdP and must not enter the local MFA gate.
          if (shouldCheckLocalMfa(provider)) {
            setMfaChecking(true);
            supabase.auth.mfa
              .getAuthenticatorAssuranceLevel()
              .then(({ data }) => {
                if (data && data.currentLevel === "aal1" && data.nextLevel === "aal2") {
                  setMfaRequired(true);
                } else if (data && data.currentLevel === "aal1" && data.nextLevel === "aal1") {
                  setMfaEnrollmentRequired(true);
                }
              })
              .catch(() => {
                // Non-blocking: if AAL check fails, don't block login
              })
              .finally(() => {
                setMfaChecking(false);
              });
          }
        }
      } else {
        // Clear Sentry user context on logout
        Sentry.setUser(null);

        // Reset login logged flag and MFA state for next session
        loginLoggedRef.current = false;
        setMfaRequired(false);
        setMfaEnrollmentRequired(false);
        sessionStorage.removeItem(LOGIN_LOGGED_KEY);

        // Remove all cached user/tenant data, not only auth-prefixed queries.
        await clearSessionQueryCache(queryClient);
      }
    });

    // Check for existing session (page reload / initial load)
    supabase.auth.getSession().then(async ({ data: { session }, error: sessionError }) => {
      if (sessionError) {
        captureAuthError("session_restore", sessionError);
      }
      setSession(session);
      setUser(session?.user ?? null);

      // On page reload, also check MFA state for email users
      if (session?.user) {
        const provider = session.user.app_metadata?.provider || "email";
        if (shouldCheckLocalMfa(provider)) {
          try {
            const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
            if (data && data.currentLevel === "aal1" && data.nextLevel === "aal2") {
              setMfaRequired(true);
            } else if (data && data.currentLevel === "aal1" && data.nextLevel === "aal1") {
              setMfaEnrollmentRequired(true);
            }
          } catch {
            // Non-blocking
          }
        }
      }

      setInitialLoading(false);
    });

    // Fallback: Force loading to false after 10 seconds to prevent infinite loader
    const loadingTimeout = setTimeout(() => {
      setInitialLoading((current) => {
        if (current) {
          logger.warn("Auth loading timeout reached - forcing loading to false");
          toast.error("Verbindungsproblem", {
            description: "Das Laden dauert zu lange. Bitte Seite neu laden.",
          });
          return false;
        }
        return current;
      });
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, [queryClient]);

  const signIn = async (email: string, password: string) => {
    // Entry log — provider + e-mail DOMAIN only, never the address or password (PII-safe).
    logger.info("signIn initiated", {
      provider: "email",
      email_domain: email.split("@")[1],
    });

    if (!(await isPasswordAuthAllowed())) {
      const error = new Error("Password authentication is disabled. Use Entra ID SSO.");
      captureAuthError("sign_in", error, {
        email_domain: email.split("@")[1],
        reason: "password_auth_disabled",
      });
      logger.warn("signIn blocked: password auth disabled", {
        provider: "email",
        email_domain: email.split("@")[1],
        reason: "password_auth_disabled",
      });
      toast.error(i18n.t("auth.passwordLoginDisabledTitle"), {
        description: i18n.t("auth.passwordLoginDisabledDesc"),
      });
      return { error };
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      captureAuthError("sign_in", error, { email_domain: email.split("@")[1] });
      // Structured log — provider + domain only, never the password, token or full address.
      //
      // Uses `logger.warn` (not `error`): `captureAuthError` above is the single,
      // suppression-aware Sentry path (it deliberately drops EXPECTED_AUTH_ERRORS like
      // invalid_credentials). `logger.error` auto-reports to Sentry, which would send
      // those expected errors anyway and duplicate real ones. `warn` stays visible in the
      // prod console (default prod level is `warn`) without auto-Sentrying.
      logger.warn("signIn failed", {
        provider: "email",
        email_domain: email.split("@")[1],
        code: error.code,
        status: error.status,
      });
      toast.error("Anmeldefehler", {
        description: error.message,
      });
      return { error };
    }
    // Check AAL level before showing success toast — don't show if MFA is still required
    try {
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalData && aalData.currentLevel === "aal1") {
        // MFA verification (aal2) or enrollment (aal1) required — skip toast
        // Toast will show after MFA completes in MfaChallenge/MfaEnrollmentScreen
        return { error: null };
      }
    } catch {
      // Non-blocking: if AAL check fails, still show toast
    }
    toast.success("Erfolgreich angemeldet", {
      description: "Willkommen zurück!",
    });
    return { error: null };
  };

  // TES-1085: signUp entfernt — Selbstregistrierung existiert nicht mehr
  // (Zugang nur per administrativer Freigabe / Entra-JIT).

  const signOut = async () => {
    // Initiated log — no user identifiers beyond the opaque session presence (PII-safe).
    logger.info("signOut initiated", { hadSession: !!session });

    // Show smooth fade-out transition overlay first
    await showLogoutTransition();

    // Reset stale guest mode for platform admins (fire-and-forget).
    //
    // TES-2031: "Guest mode" means a platform admin VISITING a tenant they hold NO
    // tenant_memberships row in (active_tenant_id set purely for browsing). Since
    // ADR-CHK-002/TES-1535 made isSuperAdmin an alias of hasRole("admin"), gating on
    // the flag alone nulled the active_tenant_id of EVERY tenant-bound admin on each
    // logout — permanently, because no login path restores it (nightly QA: admin@test.com
    // saw 0 teams). Therefore only reset when the membership query resolved successfully
    // AND found no binding to the active tenant: a real member keeps their home tenant,
    // a genuine guest (e.g. a global super_admin browsing a foreign tenant) is still reset.
    if (
      derivedValues.isSuperAdmin &&
      profile?.active_tenant_id &&
      userId &&
      tenantMembershipLoaded &&
      !tenantMembership
    ) {
      Promise.resolve(
        supabase.from("profiles").update({ active_tenant_id: null }).eq("user_id", userId),
      )
        .then(() => logger.debug("Guest mode reset on logout"))
        .catch(() => logger.warn("Failed to reset guest mode on logout (non-blocking)"));
    }

    try {
      // Clear image cache to prevent displaying cached images after logout
      clearImageCache();

      // Clear local state FIRST to prevent "Zugriff verweigert" flicker
      setUser(null);
      setSession(null);

      // Remove all cached user/tenant data immediately.
      await clearSessionQueryCache(queryClient);

      // Session-clear complete — local state + query cache wiped.
      logger.info("signOut session-clear complete");

      // Use scope: 'local' to reliably clear local token store
      // This avoids 403 errors when server session is already expired
      const { error } = await supabase.auth.signOut({ scope: "local" });

      if (error) {
        captureAuthError("sign_out", error);
        // Log but don't show to user - logout should always appear successful
        logger.debug("Logout note:", error.message);
      }
    } catch (signOutError) {
      // A storage-/lock-access exception (e.g. "Access to storage is not allowed
      // from this context") must NEVER prevent the logout redirect below — otherwise
      // the user appears stuck signed-in (observed for Super-Admins). Capture
      // non-blocking, then fall through to the guaranteed redirect.
      captureAuthError("sign_out", signOutError as Error);
      logger.debug("Logout exception (non-blocking):", signOutError);
    } finally {
      toast.success("Abgemeldet", {
        description: "Sie wurden erfolgreich abgemeldet.",
      });

      // Full page reload to /auth ensures clean state — ALWAYS runs, even if the
      // sign-out call above threw (storage / lock / network).
      window.location.href = "/auth?t=" + Date.now();
    }
  };

  const refreshProfile = async () => {
    if (userId) {
      await refetchProfile();
      // Roles will automatically refetch when profile.active_tenant_id changes
      // due to TanStack Query's key-based invalidation
    }
  };

  const reloadUserData = async () => {
    if (userId) {
      await queryClient.invalidateQueries({ queryKey: authKeys.all });
    }
  };

  // Memoized derived values
  const derivedValues = useMemo(() => {
    const hasRole = (role: AppRole) => roles.includes(role);
    const isAdmin = hasRole("admin");
    const isQualitaetsmanager = hasRole("qualitaetsmanager");
    const isMangelbearbeiter = hasRole("mangelbearbeiter");
    const isTester = hasRole("tester");
    // TES-1833 / GOAL A1: role_id-only QM-Admin (org-hull owner, non-operative).
    const isQmAdmin = hasRole("qm_admin");
    // ADR-CHK-002: super_admin folded into admin; isSuperAdmin == platform admin.
    const isSuperAdmin = hasRole("admin");
    const isTenantAdmin = tenantMembership?.is_tenant_admin ?? false;
    // Can manage inspections, checklists, schedules etc.
    const canManage = isAdmin || isQualitaetsmanager || isSuperAdmin;
    const registrationPending = profile?.registration_approved === false && !isSuperAdmin;
    // TES-626: external-identity defect capabilities (ADR 0008). Anchor-filtered
    // lists come from RLS; these flags only drive read-only UI indication.
    const isExternalReadOnlyUser = isExternalReadOnly(profile);
    const isExternalLimitedWriteUser = isExternalLimitedWrite(profile);
    const isExternalDefectUserFlag = isExternalDefectUser(profile);
    // TES-565: Kundenabnahme (customer_approver) without any defect-editing role
    // is read-only — render no status-change controls. TES-626 folds fully read-only
    // external users (partner_supplier / external_auditor) into the same flag so the
    // existing status/edit gating applies to them too.
    const isReadOnlyDefectUser = isReadOnlyDefectRole(roles) || isExternalReadOnlyUser;

    return {
      hasRole,
      isAdmin,
      isQualitaetsmanager,
      isMangelbearbeiter,
      isTester,
      isQmAdmin,
      isSuperAdmin,
      isTenantAdmin,
      canManage,
      isReadOnlyDefectUser,
      isExternalReadOnlyUser,
      isExternalLimitedWriteUser,
      isExternalDefectUser: isExternalDefectUserFlag,
      registrationPending,
    };
  }, [roles, tenantMembership, profile]);

  // Combined loading state:
  // - Initial loading (getSession check)
  // - Profile loading (only when user exists)
  // - Roles loading (only when user exists)
  const isLoading =
    initialLoading ||
    mfaChecking ||
    (!!userId && (profileLoading || rolesLoading || tenantCountLoading));

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile: profile ?? null,
        roles,
        loading: isLoading,
        signIn,
        signOut,
        refreshProfile,
        reloadUserData,
        tenantCount,
        mfaRequired,
        mfaEnrollmentRequired,
        completeMfa: () => {
          setMfaRequired(false);
          setMfaEnrollmentRequired(false);
        },
        ...derivedValues,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Safe version of useAuth that returns null instead of throwing
 * when used outside of AuthProvider. Useful for components that
 * may render before AuthProvider is fully mounted.
 */
export function useAuthSafe(): AuthContextType | null {
  const context = useContext(AuthContext);
  return context ?? null;
}
